import { msg, Trans } from '@lingui/macro'

import * as React from 'react'

import { TFUserAgreement } from '~graphql/generated/clientGraphql'
import { useTrans } from '~logic/i18n/useTrans'
import { Controller, useFormContext } from '~shared/segments/Form'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'
import { Grid } from '~ui/components/layout/Box'

import { useAgreementsCtx } from '../../contexts/AgreementProvider'
import { TOrderForm } from '../../contexts/FormProvider'
import { Agreement } from './Agreement'

type TProps = NoChildren

/**
 * User agreement form section.
 */
export const Agreements: React.FC<TProps> = () => {
  const { control } = useFormContext<TOrderForm>()
  const agreements = useAgreementsCtx()
  const { trans } = useTrans()

  // agreements that this customer has already agreed to in the past
  // (matched based on the contact information)
  const previouslyConfirmedAgreements = React.useMemo(() => {
    return agreements.filter((agreement) => {
      return agreement.wasConfirmedBefore
    })
  }, [agreements])

  // returns whether the rendered agreement was confirmed in the past or not
  // those agreements should be marked as confirmed and disabled at the same time
  // because currently we have no way of letting BE know that the customer no longer agrees with some agreement
  // (they has to change that through the speedlo-portal app)
  const wasPreviouslyConfirmed = React.useCallback(
    (agreement: TFUserAgreement) => {
      return Boolean(previouslyConfirmedAgreements.find(isSameAgreementAs(agreement)))
    },
    [previouslyConfirmedAgreements],
  )

  // return whether the rendered agreement's checkbox should be checked or not
  const isChecked = React.useCallback(
    (agreement: TFUserAgreement, confirmedAgreements: RoA<TFUserAgreement>) => {
      // first check if the customer has agreed to this agreement throughout this order flow
      const isConfirmedAgreement = Boolean(confirmedAgreements.find(isSameAgreementAs(agreement)))

      if (isConfirmedAgreement) {
        return true
      }

      // if not, check that if the customer (if we have recognized him) has agreed to this in the past
      return wasPreviouslyConfirmed(agreement)
    },
    [wasPreviouslyConfirmed],
  )

  // validate that all of the mandatory agreements
  // that the customer did not confirm in the past
  // were confirmed now
  const allMandatoryConfirmed = React.useCallback(
    (confirmedAgreements: RoA<TFUserAgreement>) => {
      // check only those agreements that were not checked already in the past
      const mandatoryAgreements = agreements.filter((agreement) => {
        return agreement.isMandatory && !agreement.wasConfirmedBefore
      })

      const allConfirmed = mandatoryAgreements.every((agreement) => {
        return confirmedAgreements.find(isSameAgreementAs(agreement))
      })

      if (allConfirmed) {
        return true
      }

      return trans(msg`Mandatory agreement is not confirmed`)
    },
    [agreements, trans],
  )

  // no need to render the section if we have no agreements (either loading or the branch has none)
  if (agreements.length === 0) {
    return null
  }

  return (
    <InputControl>
      <InputLabel>
        <Trans>Agreements</Trans>
      </InputLabel>
      <Controller
        control={control}
        name={`confirmedAgreements`}
        rules={{
          validate: allMandatoryConfirmed,
        }}
        render={({
          field: { onChange, value, name }, //
          fieldState: { invalid, error },
          formState: { isSubmitting },
        }) => (
          <Grid
            // `id` here is required for correct input focus on error
            id={name}
            rowGap={`medium`}
          >
            <>
              {agreements.map((agreement) => (
                <Agreement
                  key={agreement.id}
                  agreement={agreement}
                  isChecked={isChecked(agreement, value)}
                  isDisabled={isSubmitting || wasPreviouslyConfirmed(agreement)}
                  onChange={() => {
                    // agreement was checked before -> update the form with the rest of the values
                    // without the clicked one
                    if (isChecked(agreement, value)) {
                      const updatedValue = value.filter((confirmedAgreement) => {
                        return isSameAgreementAs(agreement)(confirmedAgreement) === false
                      })

                      onChange(updatedValue)

                      return
                    }

                    // agreement was not checked -> update the form with previously checked agreements
                    // and the newly checked one as well
                    onChange([...value, agreement])
                  }}
                />
              ))}
              <AnimatedHeight isOpen={invalid && Boolean(error?.message)}>
                <InputErrorMessage>{error?.message ?? null}</InputErrorMessage>
              </AnimatedHeight>
            </>
          </Grid>
        )}
      />
    </InputControl>
  )
}

// compares two agreements based on they ids
// the curried implementation is useful in array iterating functions
const isSameAgreementAs = (agreementA: TFUserAgreement) => (agreementB: TFUserAgreement) => {
  return agreementA.id === agreementB.id
}
